import React from "react";
import { useTranslate } from "../../lib/translation";
import styles from "./Skeleton.module.css";

export interface SkeletonProps {
  /** Shape variant. Defaults to `text`. */
  variant?: "text" | "circle" | "rect" | "avatar" | "card";
  /** Width (px or %) */
  width?: number | string;
  /** Height (px) */
  height?: number | string;
  /** Number of lines for text variant. Defaults to `3`. */
  lines?: number;
  /** Optional className */
  className?: string;
  /**
   * Visually hidden text that names what is loading, read inside the status
   * region. Defaults to the translated busyIndicator.loading string
   * ("Loading"). Ignored when decorative.
   */
  label?: string;
  /**
   * Hide this skeleton from assistive tech (aria-hidden, no status region). Use
   * it on every skeleton but one when several sketch one view, or when the
   * surrounding component already announces loading. Defaults to `false`.
   * @default false
   */
  decorative?: boolean;
  /** Animate shimmer (disabled with prefers-reduced-motion). Defaults to `true`. */
  animate?: boolean;
}

/**
 * Skeleton component.
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  variant = "text",
  width,
  height,
  lines = 3,
  className = "",
  label,
  animate = true,
  decorative = false,
}) => {
  const t = useTranslate();
  const text = label ?? t("busyIndicator.loading", "Loading");
  // A status region announces its content, not its aria-label, so the label
  // is also visually hidden text inside it; aria-label stays as the region's
  // name. Decorative skeletons carry nothing.
  const a11yProps = decorative
    ? ({ "aria-hidden": true } as const)
    : ({ role: "status", "aria-live": "polite", "aria-label": text } as const);
  const srText = decorative ? null : (
    <span className={styles.srOnly}>{text}</span>
  );

  const rootClass = [
    styles.root,
    styles[variant],
    animate ? styles.animate : styles.static,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const style: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  if (variant === "text") {
    return (
      <span
        className={rootClass}
        style={width ? { width: style.width } : {}}
        {...a11yProps}
      >
        {srText}
        {Array.from({ length: lines }).map((_, i) => (
          <span key={i} className={styles.line} />
        ))}
      </span>
    );
  }

  return (
    <span
      className={rootClass}
      style={style}
      {...a11yProps}
    >
      {srText}
    </span>
  );
};

export default Skeleton;
